import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { hostApi } from "@/lib/hostApi";
import { useHostAuth } from "@/contexts/HostAuthContext";
import { toast } from "sonner";
import {
  Crown, SignOut, Lightning, Sword, Stairs, Plus, ArrowRight, TrashSimple,
} from "@phosphor-icons/react";

const DUEL_TEMPLATES = [
  { key: "classic", label: "Classic 1v1", tagline: "Mirrored MC · highest score wins" },
  { key: "team", label: "Team Duel", tagline: "Two teams · one clock" },
  { key: "speed", label: "Speed Duel", tagline: "Fastest correct = bonus" },
  { key: "word_clash", label: "Word Clash", tagline: "Puzzles · unscramble · phrases" },
  { key: "culture_clash", label: "Culture Clash", tagline: "Emojis · pop culture · literature" },
];

export default function HostConsole() {
  const { host, loading, logout } = useHostAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [creatorOpen, setCreatorOpen] = useState(false);

  const loadEvents = async () => {
    setEventsLoading(true);
    try {
      const { data } = await hostApi.get("/live/events");
      setEvents(data.events || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load events");
    } finally { setEventsLoading(false); }
  };
  useEffect(() => { if (host) loadEvents(); }, [host]);

  if (loading) return <div className="min-h-screen bg-nk-bg text-nk-text flex items-center justify-center"><p className="text-sm text-nk-mute">Loading your console…</p></div>;
  if (!host) return <Navigate to="/host/login" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/host/login", { replace: true });
  };

  const deleteEvent = async (code) => {
    if (!window.confirm(`Delete event ${code}? This cannot be undone.`)) return;
    try {
      await hostApi.delete(`/live/events/${code}`);
      toast.success("Event deleted");
      loadEvents();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="min-h-screen bg-nk-bg text-nk-text">
      <header className="border-b border-nk-line/60 bg-nk-bg/85 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {host.brand_image_url ? (
              <img src={host.brand_image_url} alt="" className="w-8 h-8 rounded-full object-cover border border-nk-line" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-nk-gold flex items-center justify-center">
                <Crown size={14} weight="fill" className="text-nk-bg" />
              </div>
            )}
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-nk-gold/80">NKognito Live · Host</p>
              <p className="text-sm font-medium">{host.name}{host.org && <span className="text-nk-mute"> · {host.org}</span>}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="host-logout-btn"
            className="inline-flex items-center gap-2 text-xs text-nk-mute hover:text-nk-text transition-colors"
          >
            <SignOut size={14} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        <section className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-nk-gold/70 mb-2">Welcome</p>
            <h1 className="font-display text-3xl">Your Host Console.</h1>
            <p className="text-sm text-nk-mute mt-2 max-w-2xl">
              Create a new event, share the join link, and drive the show from the Host view
              while players use their phones and the audience watches the shared canvas.
            </p>
          </div>
          <button
            onClick={() => setCreatorOpen(true)}
            data-testid="host-new-event-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-nk-gold text-nk-bg font-medium hover:opacity-90 transition-opacity"
          >
            <Plus size={16} weight="bold" /> New event
          </button>
        </section>

        <section data-testid="host-events-list">
          <p className="font-mono text-[10px] uppercase tracking-widest text-nk-gold/70 mb-3">Your events</p>
          {eventsLoading ? (
            <p className="text-sm text-nk-mute">Loading…</p>
          ) : events.length === 0 ? (
            <div className="nk-card p-8 text-center border-dashed border-nk-line">
              <Lightning size={26} weight="duotone" className="mx-auto text-nk-gold/70 mb-2" />
              <p className="text-sm text-nk-mute">No events yet — click <b className="text-nk-text">New event</b> above to build your first one.</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {events.map((e) => (
                <EventRow key={e.code} event={e} onDelete={deleteEvent} />
              ))}
            </ul>
          )}
        </section>
      </main>

      {creatorOpen && (
        <EventCreator
          onClose={() => setCreatorOpen(false)}
          onCreated={(evt) => {
            setCreatorOpen(false);
            toast.success(`Event ${evt.code} created`);
            loadEvents();
            navigate(`/live/${evt.code}/host`);
          }}
        />
      )}
    </div>
  );
}


function EventRow({ event, onDelete }) {
  const ModeIcon = event.mode === "ladder" ? Stairs : Sword;
  const isDraft = event.state === "draft";
  const isRunning = event.state === "running";
  const stateStyle = isRunning
    ? "text-nk-accent border-nk-accent/60"
    : event.state === "ended"
      ? "text-nk-mute border-nk-line"
      : "text-nk-gold border-nk-gold/60";
  return (
    <li className="nk-card p-4 flex items-center gap-4" data-testid={`event-row-${event.code}`}>
      <div className="w-10 h-10 rounded-sm bg-nk-charcoal border border-nk-gold/40 flex items-center justify-center">
        <ModeIcon size={18} weight="fill" className="text-nk-gold" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium truncate">{event.title}</p>
          <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase border rounded-sm ${stateStyle}`}>
            {event.state}
          </span>
        </div>
        <p className="text-[11px] text-nk-mute font-mono">
          {event.mode.toUpperCase()}{event.template && ` · ${event.template}`} · Code{" "}
          <b className="text-nk-gold">{event.code}</b> · {event.player_count || 0} players
        </p>
      </div>
      {(isDraft || event.state === "ended") && (
        <button
          onClick={() => onDelete(event.code)}
          data-testid={`event-delete-${event.code}`}
          title="Delete event"
          className="p-2 text-nk-mute hover:text-nk-bad transition-colors"
        >
          <TrashSimple size={14} />
        </button>
      )}
      <Link
        to={`/live/${event.code}/host`}
        data-testid={`event-open-${event.code}`}
        className="inline-flex items-center gap-1 text-xs text-nk-gold hover:opacity-80"
      >
        Open <ArrowRight size={12} weight="bold" />
      </Link>
    </li>
  );
}


function EventCreator({ onClose, onCreated }) {
  const [step, setStep] = useState("mode");    // mode → template → details
  const [mode, setMode] = useState(null);
  const [template, setTemplate] = useState(null);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const canCreate = mode && template && title.trim().length >= 2;

  const submit = async () => {
    if (!canCreate) return;
    setBusy(true);
    try {
      const { data } = await hostApi.post("/live/events", {
        mode, template, title: title.trim(),
      });
      onCreated(data.event);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create event");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg nk-card p-6 space-y-5"
        data-testid="event-creator"
      >
        <div>
          <p className="eyebrow">New event · Step {step === "mode" ? 1 : step === "template" ? 2 : 3} of 3</p>
          <h2 className="font-display text-xl mt-1">
            {step === "mode" && "Pick a mode"}
            {step === "template" && "Pick a template"}
            {step === "details" && "Name your event"}
          </h2>
        </div>

        {step === "mode" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ModePick
              testid="pick-mode-duel"
              Icon={Sword} title="Duel" tagline="Head-to-head faceoff · 1v1 or teams"
              active={mode === "duel"}
              onClick={() => { setMode("duel"); setTemplate(null); setStep("template"); }}
            />
            <ModePick
              testid="pick-mode-ladder"
              Icon={Stairs} title="Ladder" tagline="10-step climb with checkpoints"
              active={mode === "ladder"}
              onClick={() => { setMode("ladder"); setTemplate("standard"); setStep("template"); }}
            />
          </div>
        )}

        {step === "template" && mode === "duel" && (
          <div className="space-y-2">
            {DUEL_TEMPLATES.map((t) => (
              <button
                key={t.key}
                onClick={() => { setTemplate(t.key); setStep("details"); }}
                data-testid={`pick-tpl-${t.key}`}
                className={`w-full text-left px-4 py-3 rounded-sm border transition-colors ${
                  template === t.key
                    ? "border-nk-gold bg-nk-gold/10"
                    : "border-nk-line hover:border-nk-gold/60"
                }`}
              >
                <p className="text-sm font-medium">{t.label}</p>
                <p className="text-[11px] text-nk-mute">{t.tagline}</p>
              </button>
            ))}
          </div>
        )}

        {step === "template" && mode === "ladder" && (
          <div className="space-y-2">
            <button
              onClick={() => { setTemplate("standard"); setStep("details"); }}
              data-testid="pick-tpl-standard"
              className="w-full text-left px-4 py-3 rounded-sm border border-nk-gold bg-nk-gold/10"
            >
              <p className="text-sm font-medium">Standard Ladder</p>
              <p className="text-[11px] text-nk-mute">10 rungs · checkpoints at 4 &amp; 8 · rising point values</p>
            </button>
          </div>
        )}

        {step === "details" && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-nk-mute mb-1">Event title</label>
              <input
                data-testid="event-title-input"
                value={title} onChange={(e) => setTitle(e.target.value)}
                autoFocus
                maxLength={80}
                placeholder="Friday Trivia Night, Lagos"
                className="w-full px-3 py-2 rounded-sm border border-nk-line bg-nk-charcoal text-sm focus:border-nk-gold outline-none"
              />
              <p className="text-[10px] text-nk-mute mt-1">Shown on the audience screen and join page.</p>
            </div>
            <div className="text-xs text-nk-mute">
              Mode: <span className="text-nk-text uppercase">{mode}</span>
              {" · "}Template: <span className="text-nk-text">{template}</span>
              <button
                onClick={() => setStep("mode")}
                className="ml-2 text-nk-gold hover:underline"
                data-testid="event-creator-back"
              >Change</button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-sm border border-nk-line text-nk-mute hover:text-nk-text"
          >
            Cancel
          </button>
          {step === "details" && (
            <button
              onClick={submit}
              disabled={!canCreate || busy}
              data-testid="event-creator-submit"
              className="px-4 py-2 text-sm rounded-sm bg-nk-gold text-nk-bg hover:opacity-90 disabled:opacity-40"
            >
              {busy ? "Creating…" : "Create event"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function ModePick({ testid, Icon, title, tagline, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-4 rounded-sm border transition-colors ${
        active ? "border-nk-gold bg-nk-gold/10" : "border-nk-line hover:border-nk-gold/60"
      }`}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 rounded-sm bg-nk-charcoal border border-nk-gold/40 flex items-center justify-center">
          <Icon size={18} weight="fill" className="text-nk-gold" />
        </div>
        <p className="font-display text-lg">{title}</p>
      </div>
      <p className="text-[11px] text-nk-mute">{tagline}</p>
    </button>
  );
}
